import React, { useState } from "react";
import { ArrowLeft, Send, CheckCircle, Briefcase, FileText, Music, Package, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Link } from "react-router-dom";

const projectTypes = [
  "Film (Feature)",
  "Film (Short)",
  "Television Series",
  "Television (Single Episode)",
  "Commercial / Advertisement",
  "Promo / Trailer",
  "Documentary",
  "Video Game",
  "Podcast / Audio",
  "Corporate / Internal",
  "Other"
];

const budgetRanges = [
  "Under $1,000",
  "$1,000 - $5,000",
  "$5,000 - $15,000",
  "$15,000 - $50,000",
  "$50,000+",
  "Let's Discuss"
];

const moodOptions = [
  "High Energy / Sports",
  "Corporate Tension / Drama",
  "Luxury / Lifestyle",
  "Dark / Cinematic",
  "Emotional / Introspective",
  "Upbeat / Transitional",
  "Custom Request"
];

const LicensingPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    projectType: "",
    projectTitle: "",
    budgetRange: "",
    requestedMood: "",
    specificTrack: "",
    needStems: "",
    deadline: "",
    additionalNotes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Build mailto link with form data
    const subject = encodeURIComponent(`Licensing Inquiry: ${formData.projectType} - ${formData.company || formData.name}`);
    const body = encodeURIComponent(`
LICENSING INQUIRY
================

Contact Information:
- Name: ${formData.name}
- Email: ${formData.email}
- Company: ${formData.company || 'N/A'}

Project Details:
- Project Type: ${formData.projectType}
- Project Title: ${formData.projectTitle || 'N/A'}
- Budget Range: ${formData.budgetRange}
- Deadline: ${formData.deadline || 'Flexible'}

Music Requirements:
- Requested Mood: ${formData.requestedMood}
- Specific Track: ${formData.specificTrack || 'Open to recommendations'}
- Need Stems: ${formData.needStems}

Additional Notes:
${formData.additionalNotes || 'None'}
    `);

    // Open mailto
    window.location.href = `mailto:leaver@itzgreenmusic.com?subject=${subject}&body=${body}`;
    
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1000);
  };

  const isFormValid = () => {
    return formData.name && formData.email && formData.projectType && formData.budgetRange && formData.requestedMood && formData.needStems;
  };

  return (
    <main className="min-h-screen bg-[rgb(17,17,19)] pt-28 pb-20 relative overflow-hidden" data-testid="licensing-page">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgb(218,255,1) 1px, transparent 1px), linear-gradient(90deg, rgb(218,255,1) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[rgb(161,161,170)] text-xs tracking-[0.2em] hover:text-[rgb(218,255,1)] transition-colors mb-10"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <ArrowLeft size={14} />
          BACK TO HOME
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <Badge
            variant="outline"
            className="border-[rgb(218,255,1)] text-[rgb(218,255,1)] bg-[rgba(218,255,1,0.05)] mb-6 px-4 py-1.5 text-[10px] tracking-[0.2em] rounded-md"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <FileText size={12} className="mr-2" />
            B2B LICENSING
          </Badge>

          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            LICENSE <span className="text-[rgb(218,255,1)]">OUR SOUND</span>
          </h1>
          <p
            className="text-[rgb(161,161,170)] text-sm sm:text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            One-stop clearance. We control both Master and Publishing rights—
            single signature clears everything.
          </p>
        </motion.div>

        {/* One-Stop Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[rgba(74,222,128,0.08)] border border-[rgba(74,222,128,0.3)] rounded-xl p-4 mb-8 flex items-center gap-3"
        >
          <CheckCircle size={20} className="text-[rgb(74,222,128)] flex-shrink-0" />
          <div>
            <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              ONE-STOP CLEARANCE GUARANTEED
            </p>
            <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              All ItzGreen Music assets are 100% cleared, human-produced, and BMI registered.
            </p>
          </div>
        </motion.div>

        {!isSubmitted ? (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Contact Info Section */}
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 sm:p-6">
              <h3 className="text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                CONTACT INFORMATION
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">NAME *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Your full name"
                    required
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                  />
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">EMAIL *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">COMPANY / NETWORK</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    placeholder="Production company, network, or agency"
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                  />
                </div>
              </div>
            </div>

            {/* Project Details Section */}
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 sm:p-6">
              <h3 className="text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                PROJECT DETAILS
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">PROJECT TYPE *</label>
                  <select
                    value={formData.projectType}
                    onChange={(e) => handleChange("projectType", e.target.value)}
                    required
                    className="w-full h-10 px-3 rounded-md bg-[rgb(40,42,45)] border border-[rgb(63,63,63)] text-white text-sm focus:border-[rgb(218,255,1)] focus:outline-none cursor-pointer"
                  >
                    <option value="">Select type</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">BUDGET RANGE *</label>
                  <select
                    value={formData.budgetRange}
                    onChange={(e) => handleChange("budgetRange", e.target.value)}
                    required
                    className="w-full h-10 px-3 rounded-md bg-[rgb(40,42,45)] border border-[rgb(63,63,63)] text-white text-sm focus:border-[rgb(218,255,1)] focus:outline-none cursor-pointer"
                  >
                    <option value="">Select range</option>
                    {budgetRanges.map((range) => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">PROJECT TITLE</label>
                  <Input
                    value={formData.projectTitle}
                    onChange={(e) => handleChange("projectTitle", e.target.value)}
                    placeholder="Working title (if applicable)"
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                  />
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">DEADLINE</label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => handleChange("deadline", e.target.value)}
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white"
                  />
                </div>
              </div>
            </div>

            {/* Music Requirements Section */}
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 sm:p-6">
              <h3 className="text-[rgb(218,255,1)] text-sm font-semibold tracking-[0.1em] mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                MUSIC REQUIREMENTS
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">REQUESTED MOOD / USE CASE *</label>
                  <select
                    value={formData.requestedMood}
                    onChange={(e) => handleChange("requestedMood", e.target.value)}
                    required
                    className="w-full h-10 px-3 rounded-md bg-[rgb(40,42,45)] border border-[rgb(63,63,63)] text-white text-sm focus:border-[rgb(218,255,1)] focus:outline-none cursor-pointer"
                  >
                    <option value="">Select mood</option>
                    {moodOptions.map((mood) => (
                      <option key={mood} value={mood}>{mood}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">SPECIFIC TRACK (IF ANY)</label>
                  <Input
                    value={formData.specificTrack}
                    onChange={(e) => handleChange("specificTrack", e.target.value)}
                    placeholder="e.g., 'Hostile Takeover' or 'Terms of Service'"
                    className="bg-[rgb(40,42,45)] border-[rgb(63,63,63)] text-white placeholder:text-[rgb(80,80,90)]"
                  />
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">NEED STEMS? *</label>
                  <div className="flex gap-4">
                    {["Yes", "No", "Maybe"].map((option) => (
                      <label key={option} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="needStems"
                          value={option}
                          checked={formData.needStems === option}
                          onChange={(e) => handleChange("needStems", e.target.value)}
                          className="w-4 h-4 accent-[rgb(218,255,1)]"
                        />
                        <span className="text-white text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[rgb(161,161,170)] text-[10px] tracking-[0.15em] block mb-2">ADDITIONAL NOTES</label>
                  <textarea
                    value={formData.additionalNotes}
                    onChange={(e) => handleChange("additionalNotes", e.target.value)}
                    placeholder="Scene description, reference tracks, special requirements..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-md bg-[rgb(40,42,45)] border border-[rgb(63,63,63)] text-white text-sm placeholder:text-[rgb(80,80,90)] focus:border-[rgb(218,255,1)] focus:outline-none resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              className="w-full bg-[rgb(218,255,1)] text-[rgb(17,17,19)] font-semibold text-sm tracking-[0.1em] h-14 rounded-xl hover:bg-[rgb(166,190,21)] hover:shadow-[0_0_30px_rgba(218,255,1,0.4)] transition-all duration-300 disabled:opacity-50"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  SENDING...
                </>
              ) : (
                <>
                  <Send size={18} className="mr-2" />
                  SUBMIT LICENSING REQUEST
                </>
              )}
            </Button>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 rounded-full bg-[rgb(74,222,128)] flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-[rgb(17,17,19)]" />
            </div>
            <h2 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
              REQUEST SUBMITTED
            </h2>
            <p className="text-[rgb(161,161,170)] text-sm mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Your licensing inquiry has been sent. We typically respond within 24-48 hours.
            </p>
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 text-[rgb(218,255,1)] text-sm font-medium hover:underline"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <Music size={14} />
              Explore the catalog while you wait
            </Link>
          </motion.div>
        )}

        <Separator className="bg-[rgb(63,63,63)] my-10" />

        {/* Direct Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <p className="text-[rgb(100,100,110)] text-xs tracking-[0.1em] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            PREFER DIRECT CONTACT?
          </p>
          <div className="space-y-1">
            <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
              Leaver Hall
            </p>
            <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              Founder & Head of Licensing, ItzGreen Music
            </p>
            <p className="text-[rgb(161,161,170)] text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
              BMI Affiliated Publisher
            </p>
            <a
              href="mailto:leaver@itzgreenmusic.com"
              className="text-[rgb(218,255,1)] text-sm font-medium hover:underline inline-block mt-2"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              leaver@itzgreenmusic.com
            </a>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default LicensingPage;
